/**
 * @license
 * Copyright© Celligence International, LLC - 2023
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
*/
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { Image, Spinner } from "react-bootstrap";
import './index.css';

// Simple Loader component
const Loader = () => (
  <div className="iframe-loader">
    <Spinner animation="border" role="status" />
    <span>Loading video...</span>
  </div>
);

// YouTube Modal component
const YouTubeModal = ({ videoId, onClose }) => {
  return (
    <div className="youtube-modal-backdrop" onClick={onClose}>
      <div className="youtube-modal-container" onClick={e => e.stopPropagation()}>
        <div className="youtube-modal-header">
          <h5>Video Player</h5>
          <button className="youtube-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="youtube-modal-body">
          <div className="youtube-iframe-container">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              title="YouTube Video"
              frameBorder="0"
              allowFullScreen
              className="youtube-video"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main VideoCarousel Component
const VideoCarousel = ({ title = "TESTIMONIALS", subheading = "Borrower Videos" }) => {
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 4,
      slidesToSlide: 3
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 4,
      slidesToSlide: 3
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
      slidesToSlide: 2
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      slidesToSlide: 1
    }
  };

  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [videoData, setVideoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch video data from YouTube playlist response
  useEffect(() => {
    async function fetchVideoData() {
      try {
        setLoading(true);
        const response = await axios.get(process.env.REACT_APP_WHYSUNWEST_VIDEO_URL);
        
        // Log the response structure for debugging
        console.log("API Response Structure:", JSON.stringify(response.data).slice(0, 300) + "...");
        
        // Safely extract video data with fallbacks
        let formattedData = [];
        const responseData = response.data;
        
        // Check if the response is already an array of items
        if (Array.isArray(responseData)) {
          formattedData = responseData.map(item => ({
            id: item.id,
            title: item.title || "",
            description: item.description || "",
            thumbnail: item.thumbnail || `https://i.ytimg.com/vi/${item.id}/mqdefault.jpg`
          }));
        }
        // Check if the response follows the YouTube API format
        else if (responseData && responseData.data && Array.isArray(responseData.data.items)) {
          formattedData = responseData.data.items.map(item => {
            // Get videoId from either contentDetails or resourceId
            let videoId = null;
            if (item.contentDetails && item.contentDetails.videoId) {
              videoId = item.contentDetails.videoId;
            } else if (item.snippet && item.snippet.resourceId && item.snippet.resourceId.videoId) {
              videoId = item.snippet.resourceId.videoId;
            }
            
            if (!videoId) return null;
            
            return {
              id: videoId,
              title: item.snippet?.title || "Untitled Video",
              description: item.snippet?.description || "",
              thumbnail: item.snippet?.thumbnails?.medium?.url || 
                        item.snippet?.thumbnails?.default?.url ||
                        `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`
            };
          }).filter(item => item !== null);
        }
        
        // If no valid data found, throw error
        if (formattedData.length === 0) {
          throw new Error("Could not extract video data from response");
        }
        
        console.log("Formatted Video Data:", formattedData.slice(0, 2));
        setVideoData(formattedData);
      } catch (error) {
        console.error("Error fetching video data:", error);
        setError("Failed to load videos. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
    
    fetchVideoData();
  }, []);

  const handleThumbnailClick = (id) => {
    setCurrentVideoId(id);
  };

  const handleCloseModal = () => {
    setCurrentVideoId(null);
  };

  const handleCustomArrowClick = (e, arrowType) => {
    e.preventDefault();
    const direction = arrowType === 'Next' ? 'right' : 'left';
    const arrowElement = document.querySelector(`.react-multiple-carousel__arrow--${direction}`);
    
    if (arrowElement) {
      arrowElement.click();
    }
  };

  if (loading) {
    return (
      <div className="gallery-section-container">
        <div className="loader-container">
          <Spinner animation="border" role="status" />
          <span>Loading videos...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gallery-section-container">
        <div className="error-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-section-container">
      <div className="top-section">
        <div className="table-title broker-videos-title">
          {subheading && <h3>{subheading}</h3>}
          {title && <h4>{title}</h4>}
        </div>
      </div>
      
      <div className="custom-arrows-div">
        <button 
          className="slider-custom-arrows arrow-left"
          onClick={(e) => handleCustomArrowClick(e, 'Previous')}
          aria-label="Previous slide"
        >
          <span aria-hidden="true">&#10094;</span>
        </button>
        <button 
          className="slider-custom-arrows arrow-right"
          onClick={(e) => handleCustomArrowClick(e, 'Next')}
          aria-label="Next slide"
        >
          <span aria-hidden="true">&#10095;</span>
        </button>
      </div>

      <div className="video-grid">
        <Carousel
          swipeable={true}
          draggable={true}
          showDots={false}
          responsive={responsive}
          infinite={false}
          centerMode={false}
          autoPlaySpeed={10000}
          keyBoardControl={true}
          autoPlay={false}
          customTransition="all .5"
          containerClass="carousel-container"
          dotListClass="custom-dot-list-style"
          itemClass="carousel-item-padding-40-px"
        >
          {videoData.map((item) => (
            <div key={item.id} className="thumbnail-container">
              <div className="image-wrapper">
                <Image
                  className="thumbnail-image"
                  src={item.thumbnail}
                  onClick={() => handleThumbnailClick(item.id)}
                  draggable="false"
                  width="320"
                  height="180"
                  alt={item.title || "Video thumbnail"}
                />
              </div>
            </div>
          ))}
        </Carousel>
      </div>

      {currentVideoId && (
        <YouTubeModal 
          videoId={currentVideoId} 
          onClose={handleCloseModal} 
        />
      )}
    </div>
  );
};

export default VideoCarousel;